import React, { useMemo } from 'react';
import styled, { css } from 'styled-components';
import createIcon from '../../asset/icon/create.png';
import deleteIcon from '../../asset/icon/delete.png';
import editIcon from '../../asset/icon/edit.png';
import Arrow from '../../shared/component/Arrow';
import HintSmallWrapper from '../../shared/component/headless/button/HintSmallWrapper';
import HintWrapper from '../../shared/component/headless/button/HintWrapper';
import MobileContextToolbar from '../../shared/component/MobileContextToolbar';
import UniversalIconBtn from '../../shared/component/headless/button/UniversalIconBtn';
import { getBtnName } from '../../shared/constants/btnNames';
import { VenueOpeningHoursData } from '../../shared/dto/EntityRepository';
import {
  DayOfWeek,
  DeviceType,
  Language,
  ModalType,
} from '../../shared/enum/EnumRepository';
import { useExhibitionVenueActions } from '../../shared/hooks/useApi/useExhibitionVenue';
import { useDeviceStore } from '../../shared/store/deviceStore';
import { useExhibitionVenueStore } from '../../shared/store/exhibitionVenueStore';
import { useLanguageStore } from '../../shared/store/languageStore';
import { useModalStore } from '../../shared/store/portal/modalStore';
import { openNewTab } from '../../shared/util/openNewTab';
import { getVenueDisplayName, getVenueTypeLabel } from '../../shared/util/venueTypeLabel';

const DAY_ORDER = [
  DayOfWeek.MONDAY,
  DayOfWeek.TUESDAY,
  DayOfWeek.WEDNESDAY,
  DayOfWeek.THURSDAY,
  DayOfWeek.FRIDAY,
  DayOfWeek.SATURDAY,
  DayOfWeek.SUNDAY,
];

const DAY_LABELS: Record<DayOfWeek, Record<Language, string>> = {
  [DayOfWeek.MONDAY]: { [Language.KR]: '월', [Language.EN]: 'Mon' },
  [DayOfWeek.TUESDAY]: { [Language.KR]: '화', [Language.EN]: 'Tue' },
  [DayOfWeek.WEDNESDAY]: { [Language.KR]: '수', [Language.EN]: 'Wed' },
  [DayOfWeek.THURSDAY]: { [Language.KR]: '목', [Language.EN]: 'Thu' },
  [DayOfWeek.FRIDAY]: { [Language.KR]: '금', [Language.EN]: 'Fri' },
  [DayOfWeek.SATURDAY]: { [Language.KR]: '토', [Language.EN]: 'Sat' },
  [DayOfWeek.SUNDAY]: { [Language.KR]: '일', [Language.EN]: 'Sun' },
};

const getLocalizedText = (
  korean: string | null | undefined,
  english: string | null | undefined,
  language: Language,
): string => (
  language === Language.EN
    ? english?.trim() || korean?.trim() || ''
    : korean?.trim() || ''
);

const cleanText = (value: string | null | undefined): string => value?.trim() ?? '';

const formatTime = (time: string): string => time.slice(0, 5);

const sortOpeningHours = (
  openingHours: VenueOpeningHoursData[],
): VenueOpeningHoursData[] => [...openingHours].sort(
  (a, b) => DAY_ORDER.indexOf(a.dayOfWeek) - DAY_ORDER.indexOf(b.dayOfWeek),
);

interface VenueDetailContainerProps {
  showRequestActions?: boolean;
}

const VenueDetailContainer: React.FC<VenueDetailContainerProps> = ({
  showRequestActions = false,
}) => {
  const language = useLanguageStore((state) => state.language);
  const isMobile = useDeviceStore((state) => state.deviceType === DeviceType.MOBILE);
  const selectedVenueId = useExhibitionVenueStore((state) => state.selectedVenueId);
  const detail = useExhibitionVenueStore((state) => (
    state.displayedVenueId
      ? state.venueDetailCache[state.displayedVenueId]
      : undefined
  ));
  const indexVenue = useExhibitionVenueStore((state) => {
    const venueId = state.displayedVenueId ?? state.selectedVenueId;
    return venueId
      ? state.venueList.find((venue) => String(venue.id) === venueId)
      : undefined;
  });
  const { clearVenueDetail } = useExhibitionVenueActions();
  const setStandardModal = useModalStore((state) => state.setStandardModal);
  const venueName = getVenueDisplayName({
    name: detail?.name ?? indexVenue?.name ?? '',
    nameEn: detail?.nameEn ?? indexVenue?.nameEn ?? '',
  }, language);
  const description = detail
    ? getLocalizedText(detail.description, detail.descriptionEn, language)
    : '';
  const address = detail
    ? getLocalizedText(detail.roadAddress, detail.roadAddressEn, language)
    : '';
  const mapUrl = detail
    ? (language === Language.KR
      ? cleanText(detail.naverMapUrl) || cleanText(detail.googleMapUrl)
      : cleanText(detail.googleMapUrl) || cleanText(detail.naverMapUrl))
    : '';
  const openingHoursNote = cleanText(detail?.openingHoursNote);
  const websiteUrl = cleanText(detail?.websiteUrl);
  const instagramUrl = cleanText(detail?.instagramUrl);
  const openingHours = useMemo(
    () => sortOpeningHours(detail?.openingHours ?? []),
    [detail?.openingHours],
  );
  if (!selectedVenueId) return null;

  const closeLabel = language === Language.KR
    ? `${venueName} 공간 정보 닫기`
    : `Close details for ${venueName}`;
  const registerVenueLabel = getBtnName('REGISTER_VENUE');
  const changeRequestLabel = getBtnName('REQUEST_VENUE_INFORMATION_CHANGE');

  const openVenueRequest = (isInformationChangeRequest: boolean) => {
    setStandardModal({
      modalType: ModalType.VENUE_REQUEST,
      title: null,
      value: isInformationChangeRequest ? venueName : null,
      handleChange: () => {},
      option: isInformationChangeRequest,
    });
  };

  return (
    <Container aria-label={language === Language.KR ? '공간 정보' : 'Venue details'}>
      <CloseButtonContainer>
        <UniversalIconBtn
          size="small"
          border={false}
          isTransparent
          hoverOpacity
          backgroundImg={deleteIcon}
          aria-label={closeLabel}
          onClick={clearVenueDetail}
        />
      </CloseButtonContainer>
      <DetailScroll $hasActions={showRequestActions} $isMobile={isMobile}>
        <VenueName>{venueName}</VenueName>
        {detail && (
          <>
            <VenueType>{getVenueTypeLabel(detail.type, language)}</VenueType>
            {description && <Description>{description}</Description>}
            {address && (
              <Section>
                <Value>{address}</Value>
                {mapUrl && (
                  <MapLinkButton type="button" onClick={() => openNewTab(mapUrl)}>
                    <span>{language === Language.KR ? '지도에서 보기' : 'View map'}</span>
                    <LinkArrow aria-hidden="true"><Arrow /></LinkArrow>
                  </MapLinkButton>
                )}
              </Section>
            )}
            {(openingHours.length > 0 || openingHoursNote) && (
              <Section>
                <SectionTitle>{language === Language.KR ? '운영시간' : 'Hours'}</SectionTitle>
                {openingHours.map((hour) => (
                  <HoursRow key={hour.dayOfWeek}>
                    <Day>{DAY_LABELS[hour.dayOfWeek][language]}</Day>
                    <Value>
                      {hour.closed
                        ? (language === Language.KR ? '휴관' : 'Closed')
                        : `${formatTime(hour.opensAt)}–${formatTime(hour.closesAt)}`}
                    </Value>
                  </HoursRow>
                ))}
                {openingHoursNote && (
                  <MutedText>{openingHoursNote}</MutedText>
                )}
              </Section>
            )}
            {(websiteUrl || instagramUrl) && (
              <Links>
                {instagramUrl && (
                  <ExternalLinkButton type="button" onClick={() => openNewTab(instagramUrl)}>
                    <span>Instagram</span>
                    <LinkArrow aria-hidden="true"><Arrow /></LinkArrow>
                  </ExternalLinkButton>
                )}
                {websiteUrl && (
                  <ExternalLinkButton type="button" onClick={() => openNewTab(websiteUrl)}>
                    <span>Website</span>
                    <LinkArrow aria-hidden="true"><Arrow /></LinkArrow>
                  </ExternalLinkButton>
                )}
              </Links>
            )}
          </>
        )}
      </DetailScroll>
      {showRequestActions && (
        isMobile ? (
          <MobileContextToolbar>
            <HintSmallWrapper content={registerVenueLabel} toLeft>
              <UniversalIconBtn
                size="fit"
                border={false}
                isTransparent
                backgroundImg={createIcon}
                aria-label={registerVenueLabel}
                onClick={() => openVenueRequest(false)}
              />
            </HintSmallWrapper>
            <HintWrapper content={changeRequestLabel} toLeft>
              <UniversalIconBtn
                size="fit"
                border={false}
                isTransparent
                backgroundImg={editIcon}
                aria-label={changeRequestLabel}
                onClick={() => openVenueRequest(true)}
              />
            </HintWrapper>
          </MobileContextToolbar>
        ) : (
          <ActionBar>
            <HintSmallWrapper content={registerVenueLabel} toLeft>
              <UniversalIconBtn
                size="small"
                border={false}
                isTransparent
                backgroundImg={createIcon}
                aria-label={registerVenueLabel}
                onClick={() => openVenueRequest(false)}
              />
            </HintSmallWrapper>
            <HintWrapper content={changeRequestLabel} toLeft>
              <UniversalIconBtn
                size="small"
                border={false}
                isTransparent
                backgroundImg={editIcon}
                aria-label={changeRequestLabel}
                onClick={() => openVenueRequest(true)}
              />
            </HintWrapper>
          </ActionBar>
        )
      )}
    </Container>
  );
};

const Container = styled.section`
  position: relative;
  width: 100%;
  height: 100%;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-sizing: border-box;
`;

const DetailScroll = styled.div<{ $hasActions: boolean; $isMobile: boolean }>`
  flex: 1;
  min-height: 0;
  padding-right: 28px;
  padding-bottom: ${({ $hasActions, $isMobile }) => {
    if (!$hasActions) return '0';
    return $isMobile ? '80px' : '30px';
  }};
  overflow-x: hidden;
  overflow-y: auto;
  box-sizing: border-box;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const CloseButtonContainer = styled.div`
  position: absolute;
  top: -4px;
  right: 0;
  display: flex;
`;

const ActionBar = styled.div`
  position: absolute;
  right: 0;
  bottom: 0.6vh;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 0.5vh;
  color: ${({ theme }) => theme.colors.color_Gray_03};
  ${({ theme }) => theme.typography.Body_04};
`;

const VenueName = styled.h2`
  width: fit-content;
  max-width: 100%;
  margin: 0;
  overflow-wrap: anywhere;
  color: ${({ theme }) => theme.colors.color_Gray_02};
  ${({ theme }) => theme.typography.Body_03_2};
`;

const VenueType = styled.div`
  margin-top: 3px;
  color: ${({ theme }) => theme.colors.color_Gray_04};
  ${({ theme }) => theme.typography.Footer_01};
`;

const Description = styled.p`
  margin: 16px 0 0;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  color: ${({ theme }) => theme.colors.color_Gray_03};
  ${({ theme }) => theme.typography.Body_04};
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  margin-top: 16px;
`;

const SectionTitle = styled.div`
  color: ${({ theme }) => theme.colors.color_Gray_04};
  ${({ theme }) => theme.typography.Footer_01};
`;

const HoursRow = styled.div`
  display: grid;
  grid-template-columns: 32px minmax(0, 1fr);
  gap: 8px;
`;

const Day = styled.span`
  color: ${({ theme }) => theme.colors.color_Gray_04};
  ${({ theme }) => theme.typography.Body_04};
`;

const Value = styled.div`
  overflow-wrap: anywhere;
  color: ${({ theme }) => theme.colors.color_Gray_03};
  ${({ theme }) => theme.typography.Body_04};
`;

const MutedText = styled.div`
  margin-top: 3px;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  color: ${({ theme }) => theme.colors.color_Gray_04};
  ${({ theme }) => theme.typography.Body_04};
`;

const Links = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 3px;
  margin-top: 16px;
`;

const LinkArrow = styled.span`
  width: 12px;
  height: 12px;
  display: flex;
  flex-shrink: 0;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s ease-in-out;

  svg {
    width: 10px;
    height: 10px;
    padding-top: 0;
  }
`;

const linkButtonStyle = css`
  margin: 0;
  padding: 0;
  border: 0;
  background: transparent;
  text-align: left;
  text-decoration: underline;
  cursor: pointer;

  &:hover ${LinkArrow},
  &:focus-visible ${LinkArrow} {
    opacity: 1;
  }
`;

const MapLinkButton = styled.button`
  ${linkButtonStyle}
  width: fit-content;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: ${({ theme }) => theme.colors.color_Gray_03};
  ${({ theme }) => theme.typography.Body_04};
`;

const ExternalLinkButton = styled.button`
  ${linkButtonStyle}
  display: grid;
  grid-template-columns: 72px 12px;
  align-items: center;
  column-gap: 8px;
  color: ${({ theme }) => theme.colors.color_Gray_03};
  ${({ theme }) => theme.typography.Body_04};
`;

export default VenueDetailContainer;
